import React, { useState, useEffect } from 'react';
import { adminApi } from './AdminLayout';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const LEASE_TYPES = [
  { value: 'placement', label: 'Placement' },
  { value: 'purchase', label: 'Purchase' },
  { value: 'lease', label: 'Lease' },
];

const DeploymentPanel = ({ token, deployment, onUpdate }) => {
  const [catalog, setCatalog] = useState(null);
  const [form, setForm] = useState({
    tier: deployment?.tier?.toString() || '',
    terminalModel: deployment?.terminalModel || '',
    quantity: deployment?.quantity?.toString() || '1',
    leaseType: deployment?.leaseType || '',
    leaseTerm: deployment?.leaseTerm?.toString() || '',
    simRequired: deployment?.simRequired || false,
    notes: deployment?.notes || '',
    shippingAddress: {
      street: deployment?.shippingAddress?.street || '',
      city: deployment?.shippingAddress?.city || '',
      state: deployment?.shippingAddress?.state || '',
      zip: deployment?.shippingAddress?.zip || '',
    },
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get('/admin/terminal-catalog')
      .then((res) => setCatalog(res.data))
      .catch(() => toast.error('Failed to load terminal catalog'));
  }, []);

  const selectedTierData = catalog ? catalog[parseInt(form.tier)] : null;
  const availableDevices = selectedTierData?.devices || [];
  const monthlyFee = selectedTierData?.monthlyFee;

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateAddress = (field, value) => {
    setForm((prev) => ({
      ...prev,
      shippingAddress: { ...prev.shippingAddress, [field]: value },
    }));
  };

  const handleTierChange = (value) => {
    setForm((prev) => ({ ...prev, tier: value, terminalModel: '' }));
  };

  const handleSave = async () => {
    if (!form.tier) {
      toast.error('Please select a tier');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        tier: parseInt(form.tier),
        terminalModel: form.terminalModel || null,
        quantity: parseInt(form.quantity) || 1,
        leaseType: form.leaseType || null,
        leaseTerm: form.leaseTerm ? parseInt(form.leaseTerm) : null,
        simRequired: form.simRequired,
        notes: form.notes || null,
        shippingAddress: form.shippingAddress,
      };
      const response = await adminApi.patch(`/admin/applications/${token}/deployment`, payload);
      toast.success('Deployment saved');
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save deployment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
      <h3 className="text-lg font-semibold text-slate-100 mb-4">Deployment Configuration</h3>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-200">Tier *</Label>
            <Select value={form.tier} onValueChange={handleTierChange}>
              <SelectTrigger className="mt-1 bg-slate-900 border-slate-700 text-slate-100" data-testid="tier-select">
                <SelectValue placeholder="Select tier" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="1" className="text-slate-200 focus:bg-slate-700 focus:text-slate-100">
                  Tier 1 — ${catalog?.[1]?.monthlyFee || 20}/mo
                </SelectItem>
                <SelectItem value="2" className="text-slate-200 focus:bg-slate-700 focus:text-slate-100">
                  Tier 2 — ${catalog?.[2]?.monthlyFee || 30}/mo
                </SelectItem>
              </SelectContent>
            </Select>
            {monthlyFee != null && (
              <p className="text-xs text-slate-400 mt-1">${monthlyFee.toFixed(2)}/month per terminal</p>
            )}
          </div>

          <div>
            <Label className="text-slate-200">Device Model</Label>
            <Select
              value={form.terminalModel}
              onValueChange={(v) => updateField('terminalModel', v)}
              disabled={!form.tier}
            >
              <SelectTrigger className="mt-1 bg-slate-900 border-slate-700 text-slate-100" data-testid="device-select">
                <SelectValue placeholder={form.tier ? 'Select device' : 'Select tier first'} />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {availableDevices.map((device) => (
                  <SelectItem key={device} value={device} className="text-slate-200 focus:bg-slate-700 focus:text-slate-100">
                    {device}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-slate-200">Quantity</Label>
            <Input
              type="number"
              min="1"
              value={form.quantity}
              onChange={(e) => updateField('quantity', e.target.value)}
              className="mt-1 bg-slate-900 border-slate-700 text-slate-100"
              data-testid="quantity-input"
            />
          </div>
          <div>
            <Label className="text-slate-200">Lease Type</Label>
            <Select value={form.leaseType} onValueChange={(v) => updateField('leaseType', v)}>
              <SelectTrigger className="mt-1 bg-slate-900 border-slate-700 text-slate-100">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {LEASE_TYPES.map((lt) => (
                  <SelectItem key={lt.value} value={lt.value} className="text-slate-200 focus:bg-slate-700 focus:text-slate-100">
                    {lt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-200">Lease Term (months)</Label>
            <Input
              type="number"
              min="1"
              value={form.leaseTerm}
              onChange={(e) => updateField('leaseTerm', e.target.value)}
              className="mt-1 bg-slate-900 border-slate-700 text-slate-100"
            />
          </div>
        </div>

        <div>
          <Label className="text-slate-200 mb-2 block">Shipping Address</Label>
          <div className="grid grid-cols-2 gap-3">
            <Input
              placeholder="Street"
              value={form.shippingAddress.street}
              onChange={(e) => updateAddress('street', e.target.value)}
              className="bg-slate-900 border-slate-700 text-slate-100"
            />
            <Input
              placeholder="City"
              value={form.shippingAddress.city}
              onChange={(e) => updateAddress('city', e.target.value)}
              className="bg-slate-900 border-slate-700 text-slate-100"
            />
            <Input
              placeholder="State"
              value={form.shippingAddress.state}
              onChange={(e) => updateAddress('state', e.target.value)}
              className="bg-slate-900 border-slate-700 text-slate-100"
            />
            <Input
              placeholder="ZIP"
              value={form.shippingAddress.zip}
              onChange={(e) => updateAddress('zip', e.target.value)}
              className="bg-slate-900 border-slate-700 text-slate-100"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="simRequired"
            checked={form.simRequired}
            onCheckedChange={(checked) => updateField('simRequired', !!checked)}
            className="border-slate-600 data-[state=checked]:bg-slate-200 data-[state=checked]:text-slate-900"
          />
          <Label htmlFor="simRequired" className="text-slate-300 text-sm cursor-pointer">
            SIM card required
          </Label>
        </div>

        <div>
          <Label className="text-slate-200">Notes</Label>
          <textarea
            value={form.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            rows={2}
            className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 text-slate-100 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-slate-200 text-slate-900 hover:bg-slate-100 font-medium"
          data-testid="save-deployment-btn"
        >
          {saving ? 'Saving...' : 'Save Deployment'}
        </Button>
      </div>
    </div>
  );
};

export default DeploymentPanel;
